import * as React from "react";
import { connect, shallowEqual } from "react-redux";
import { useEffect, useState } from "react";
import { debounce } from "lodash";

import { CandyDispatch } from "../../../Engine/Core/Store/CandyReduxTypes";
import { FullFormState } from "../../../Engine/Core/Store/FullFormState";
import { ViewModelSelect } from "../../../Helpers/StateAccessors/ViewModelSelect";
import { withControlDebugInfo } from "../../withControlDebugInfo";
import { withResolvePaths } from "../../withResolvePaths";
import { withViewModelSelect } from "../../withViewModelSelect";
import { withExternalMountingEventReporting } from "../../Services/ExternalMountingEventReporting/withExternalMountingEventReporting";
import { withFocusManagement } from "../../Services/FocusManagement/withFocusManagement";
import { withTestPageObjectRegistration } from "../../Services/TestPageObjects/PageObjectExposure";
import { EvaluableProp, withEvaluators } from "../../withEvaluators";
import {
    AbsoluteResolvedModelPath,
    createAbsoluteFromTokens,
    createModelPath,
    PathTokens,
    RelativeModelPath,
    RelativeResolvedModelPath,
} from "../../../../../Common/ModelPath/ModelPath";
import { BindingPath } from "../../../Engine/Core/CoreTypes";
import { UIUtil } from "../../../Engine/Form/CustomizationContexts/HelperFunctionContext/UIUtil/UIUtil";
import { IterUtils } from "../../../../../Common/IterableUtils";
import { delay } from "../../../Engine/utils";
import { runFloatingAsyncAction } from "../../../Helpers/TypingUtils";
import { isNotNullOrEmpty, noop, reject } from "../../../../../Common/TypingUtils";
import { IUpdateFormModelAccessor } from "../../../Engine/Form/IFormModelAccessor";
import { AbsoluteModelFieldPath } from "../../../../../Common/ModelPath/AbsoluteModelFieldPath";
import { buildDirtyPathForMultiplicityAndCheckboxPath } from "../../FastSelectAllCheckboxesHelper";
import { InnerQueryRequest } from "../../../../../Common/CandyApiClient/Types/QueryCollecton/InnerQueryRequest";
import { SelectClause } from "../../../../../Common/CandyApiClient/Types/QueryCollecton/SelectClause";
import { CountCallExpression } from "../../../../../Common/CandyApiClient/Types/QueryCollecton/Expressions/CountCallExpression";
import { FilterExpressionToQueryCollectionExpressionVisitor } from "../../../Helpers/FilterExpressions/FilterExpressionToQueryCollectionExpressionVisitor";
import { StringMatchExpression } from "../../../../../Common/CandyApiClient/Types/QueryCollecton/Expressions/StringMatchExpression";
import { AndExpression } from "../../../../../Common/CandyApiClient/Types/QueryCollecton/Expressions/AndExpression";
import { withDisabledByParent } from "../../withDisabledByParent";
import { useTypedDispatch, useTypedSelector } from "../../../../../Common/UseTypedSelector";
import { useMultilineClient } from "../../../Engine/Core/Filtering/UseMultilineClient";
import { IMultilineManagerClient } from "../../../Engine/Core/Filtering/MultilineManager";

import { SelectAllCheckbox, SelectAllCheckboxProps } from "./SelectAllCheckbox";
import { extractDirtyCheckboxesArray } from "./SelectAllCheckboxHelper";

const ClientModeDebounceDelay = 100;
const ServerModeDebounceDelay = 500;

interface SelectAllCheckboxContainerProps extends SelectAllCheckboxProps {
    bindingPath: AbsoluteResolvedModelPath;
    pathToChecked: BindingPath;
    filteredUniqKey: string;
    checkedValue?: string;
    uncheckedValue?: string;
    viewModelSelect: ViewModelSelect;
    disabled2?: EvaluableProp<boolean>;
    dirtyModeInstancesCount?: number;
    processEvaluableProp: <T>(state: FullFormState, prop: EvaluableProp<T>) => T;
}

const mapStateToProps = (state: FullFormState, ownProps: SelectAllCheckboxContainerProps) => {
    const { checkedValue, disabled2 } = ownProps;

    return {
        disabled: ownProps.processEvaluableProp(state, disabled2),
        isReadOnly: state.settings.isReadOnly,
        checkedValue: checkedValue ?? "true",
        isFormReloading: state.formVisualState.isFormReloading,
    };
};

const thresholdValue = 1000;
const defaultBatchSize = 3000;

const performChanges = async (
    form: IForm,
    _state: FullFormState,
    bindingPath: AbsoluteResolvedModelPath,
    filteredUniqKey: string,
    pathToChecked: string,
    modelValue: string,
    checkedValue: string | boolean,
    dirtyModeInstancesCount?: number
) => {
    const multilineManager = form.filteringManager.getMultilineManager(bindingPath, filteredUniqKey);
    const useServerFilters = multilineManager.getCurrentState().useServerFilter;
    const instances: readonly string[] = multilineManager.getCurrentState().displayedInstances;
    const performClientSideValueSetAsync = async (batchSize: number) => {
        const batches = IterUtils.batch(instances, batchSize);
        for (const batch of batches) {
            await delay(0);
            form.updateModel(model => {
                for (const instance of batch) {
                    const pathToCheckedForInstance = bindingPath
                        .joinWith(instance)
                        .joinWith(createModelPath(pathToChecked, false));
                    model.setValue(pathToCheckedForInstance, modelValue);
                }
            });
        }
    };

    const pathToStoredDirtyMasks = createAbsoluteFromTokens([
        IterUtils.first(bindingPath.getPathParts()) ?? reject(),
    ]) as AbsoluteResolvedModelPath;

    const getDirtyMasksValues = (model: IUpdateFormModelAccessor) => {
        const currentValueFromModel = model.getViewModelField(pathToStoredDirtyMasks, "dirtyChangedPathMasks") ?? "[]";
        const currentValue = extractDirtyCheckboxesArray(currentValueFromModel);
        const currentArray = Array.isArray(currentValue) ? currentValue : [];
        return currentArray;
    };

    const isDirtySetValuesRequired = () => {
        const currentArray = getDirtyMasksValues(form);
        return (
            currentArray.length > 0 ||
            (dirtyModeInstancesCount != undefined && instances.length >= dirtyModeInstancesCount)
        );
    };

    const performSetFastAsync = async () => {
        form.updateModel(model => {
            const pathToCheckedArrayForInstance = buildDirtyPathForMultiplicityAndCheckboxPath(
                bindingPath,
                pathToChecked
            );
            const instancesToStore = modelValue === "true" ? instances : [];
            const currentDirtyCheckedInstancesValue = model.getValue(pathToCheckedArrayForInstance) ?? "[]";
            const currentDirtyCheckedInstancesSet = new Set(
                extractDirtyCheckboxesArray(currentDirtyCheckedInstancesValue)
            );
            for (const instance of instances) {
                if (modelValue === checkedValue) {
                    currentDirtyCheckedInstancesSet.add(instance);
                } else {
                    currentDirtyCheckedInstancesSet.delete(instance);
                }
            }

            model.setValue(pathToCheckedArrayForInstance, JSON.stringify(instancesToStore));

            const checkedMask = new AbsoluteModelFieldPath(
                bindingPath.joinWith(PathTokens.each).joinWith(createModelPath(pathToChecked, false)),
                "value"
            );

            const pathToCheckedArrayForInstanceWithField = new AbsoluteModelFieldPath(
                pathToCheckedArrayForInstance,
                "value"
            );

            const pathToStoredDirtyMasksWithFields = new AbsoluteModelFieldPath(
                pathToStoredDirtyMasks,
                "dirtyChangedPathMasks"
            );

            const currentArray = getDirtyMasksValues(model);
            const updatedArray = [
                ...new Set(currentArray)
                    .add(checkedMask.toString(true))
                    .add(pathToCheckedArrayForInstanceWithField.toString(true))
                    .add(pathToStoredDirtyMasksWithFields.toString(true))
                    .values(),
            ];
            model.setViewModelField(pathToStoredDirtyMasks, "dirtyChangedPathMasks", JSON.stringify(updatedArray));
        });
    };

    const performServerSideValueSetAsync = async () => {
        if (isDirtySetValuesRequired()) {
            await performSetFastAsync();
        } else {
            await form.setMultipleWithNormalization([
                {
                    instances: instances,
                    path: bindingPath,
                    changingFields: [
                        {
                            fieldName: `${pathToChecked}.value`,
                            value: modelValue,
                        },
                    ],
                },
            ]);
        }
    };

    const runWithBlockingModalIfNeededAction =
        instances.length > thresholdValue || useServerFilters
            ? (action: () => Promise<void>) =>
                  new UIUtil(form.settings.theme).executeLongOperation(
                      { title: "Выполняется действие, ожидайте..", modalSpecialCase: "InfiniteProcess" },
                      async () => {
                          await action();
                      }
                  )
            : (action: () => Promise<void>) => action();

    const setValuesAction =
        form.settings.useServerSideFLangNormalization &&
        (instances.length > thresholdValue || useServerFilters || isDirtySetValuesRequired())
            ? () => performServerSideValueSetAsync()
            : () => performClientSideValueSetAsync(defaultBatchSize);

    await runWithBlockingModalIfNeededAction(() => setValuesAction());
};

const mapDispatchToProps = (dispatch: CandyDispatch, ownProps: SelectAllCheckboxContainerProps) => ({
    onChangeAsync: async (value: boolean) => {
        const modelValue =
            value && ownProps.checkedValue != undefined
                ? ownProps.checkedValue
                : !value && ownProps.uncheckedValue != undefined
                  ? ownProps.uncheckedValue
                  : value.toString();

        await dispatch(async (_, getState, form) => {
            const state = getState();

            await performChanges(
                form,
                state,
                ownProps.bindingPath,
                ownProps.filteredUniqKey,
                ownProps.pathToChecked,
                modelValue,
                ownProps.checkedValue ?? "true",
                ownProps.dirtyModeInstancesCount
            );
        });
    },
    dispatch: dispatch,
});

const recalculateCheckedCountersOnClient = async (
    multilineManager: IMultilineManagerClient,
    abortSignal: AbortSignal,
    dispatch: CandyDispatch,
    bindingPath: AbsoluteResolvedModelPath,
    viewModelSelect: ViewModelSelect,
    pathToChecked: RelativeResolvedModelPath,
    checkedValue: string | undefined,
    setIsInProcessing: (flag: boolean) => void,
    setCounters: (counters: { visibleCount: number; checkedInVisibleCount: number }) => void
) => {
    await dispatch(async (_, getState) => {
        const visibleInstances = multilineManager.getCurrentState().displayedInstances;

        if (abortSignal.aborted) {
            return;
        }
        try {
            const state = getState();
            setIsInProcessing(true);
            await delay(0);

            let checkedInVisibleCount = 0;
            const pathToCheckedArrayForInstance = buildDirtyPathForMultiplicityAndCheckboxPath(
                bindingPath,
                pathToChecked
            );
            const checkedFastInstances = viewModelSelect.pathViewModel(state, pathToCheckedArrayForInstance)?.value;
            if (isNotNullOrEmpty(checkedFastInstances)) {
                const checkedFastInstancesArray = new Set(extractDirtyCheckboxesArray(checkedFastInstances));
                checkedInVisibleCount = IterUtils.count(
                    Iterator.from(visibleInstances).filter(instance => checkedFastInstancesArray.has(instance))
                );
            } else {
                for (const [index, instance] of IterUtils.iterateEntries(visibleInstances)) {
                    const path = bindingPath.joinWith(instance, pathToChecked);
                    const modelValue = viewModelSelect.pathViewModel(state, path)?.value;
                    checkedInVisibleCount =
                        modelValue === checkedValue ? checkedInVisibleCount + 1 : checkedInVisibleCount;
                    if (index % 10000 == 0) {
                        await delay(0);
                        if (abortSignal.aborted) {
                            return;
                        }
                    }
                }
            }

            if (abortSignal.aborted) {
                return;
            }
            setCounters({
                visibleCount: visibleInstances.length,
                checkedInVisibleCount: checkedInVisibleCount,
            });
        } finally {
            if (!abortSignal.aborted) {
                setIsInProcessing(false);
            }
        }
    });
};

const recalculateCheckedCountersOnServer = async (
    multilineManager: IMultilineManagerClient,
    abortSignal: AbortSignal,
    dispatch: CandyDispatch,
    bindingPath: AbsoluteResolvedModelPath,
    _filteredUniqKey: string,
    _viewModelSelect: ViewModelSelect,
    pathToChecked: RelativeModelPath,
    checkedValue: string | undefined,
    setIsInProcessing: (flag: boolean) => void,
    setCounters: (counters: { visibleCount: number; checkedInVisibleCount: number }) => void
) => {
    await dispatch(async (_, __, form) => {
        if (abortSignal.aborted) {
            return;
        }
        const { displayedInstances, filters } = multilineManager.getCurrentState();
        const visibleInstancesCount = displayedInstances.length;
        const filterConverter = new FilterExpressionToQueryCollectionExpressionVisitor(bindingPath);
        const convertedMultilineFilters = filters.map(x => filterConverter.transformExpression(x.expression));
        const allFilters = convertedMultilineFilters.concat(
            new StringMatchExpression(pathToChecked.toLegacyPath() + ".value", checkedValue ?? "true", "Exact", true)
        );
        const combinedFilter = allFilters.length > 1 ? new AndExpression(allFilters) : allFilters?.[0];

        const candyApiDraftClient = form.candyApiDraftClient;
        const request = new InnerQueryRequest(
            bindingPath.toLegacyPath(),
            [new SelectClause("CheckedCount", new CountCallExpression())],
            combinedFilter,
            undefined,
            undefined
        );

        try {
            setIsInProcessing(true);

            await form.changeSet.send();

            const response = await candyApiDraftClient.queryCollection(request);
            // eslint-disable-next-line no-constant-binary-expression
            const checkedInVisibleCount = Number(response.data?.[0]?.[0]) ?? 0;

            if (abortSignal.aborted) {
                return;
            }
            setCounters({
                visibleCount: visibleInstancesCount,
                checkedInVisibleCount: checkedInVisibleCount,
            });
        } finally {
            if (!abortSignal.aborted) {
                setIsInProcessing(false);
            }
        }
    });
};

export const SelectAllCheckboxWrapper = (props: SelectAllCheckboxContainerProps) => {
    const dispatch = useTypedDispatch();
    const { disabled, checkedValue, isReadOnly, isFormReloading } = useTypedSelector(
        state => mapStateToProps(state, props),
        shallowEqual
    );

    const [counters, setCounters] = useState({ visibleCount: 0, checkedInVisibleCount: 0 });
    const [isInProcessing, setIsInProcessing] = useState(false);
    const pathToChecked = createModelPath(props.pathToChecked, false);

    const {
        client,
        loading: multilineIsInLoading,
        filteringResultVersion,
        useServerFilter: useServerMode,
        displayedInstances,
    } = useMultilineClient(props.bindingPath, props.filteredUniqKey, info => ({
        client: info.client,
        loading: info.multilineState.loading,
        filteringResultVersion: info.multilineState.filteringResultVersion,
        useServerFilter: info.multilineState.useServerFilter,
        displayedInstances: info.multilineState.displayedInstances,
    }));

    useEffect(() => {
        if (isFormReloading) {
            return noop;
        }

        const debouncedHandler = debounce(
            (signal: AbortSignal) =>
                useServerMode
                    ? recalculateCheckedCountersOnServer(
                          client,
                          signal,
                          dispatch,
                          props.bindingPath,
                          props.filteredUniqKey,
                          props.viewModelSelect,
                          pathToChecked,
                          checkedValue,
                          setIsInProcessing,
                          setCounters
                      )
                    : recalculateCheckedCountersOnClient(
                          client,
                          signal,
                          dispatch,
                          props.bindingPath,
                          props.viewModelSelect,
                          pathToChecked,
                          checkedValue,
                          setIsInProcessing,
                          setCounters
                      ),
            useServerMode ? ServerModeDebounceDelay : ClientModeDebounceDelay
        );

        const abortController = new AbortController();
        let currentNestedAbortController: AbortController;
        const eventHandlerDebounced = debounce(async () => {
            if (currentNestedAbortController != undefined) {
                currentNestedAbortController.abort();
            }
            const nextAbortController = new AbortController();
            currentNestedAbortController = nextAbortController;
            const abortListenerForNested = () => nextAbortController.abort();
            try {
                abortController.signal.addEventListener("abort", abortListenerForNested);
                await debouncedHandler(currentNestedAbortController.signal);
            } finally {
                abortController.signal.removeEventListener("abort", abortListenerForNested);
            }
        }, 20);
        const subscription = dispatch((_, __, form) =>
            form.onModelChange(
                [
                    props.bindingPath
                        .joinWith(PathTokens.each)
                        .joinWith(createModelPath(props.pathToChecked, false))
                        .toLegacyPath(),
                    buildDirtyPathForMultiplicityAndCheckboxPath(props.bindingPath, pathToChecked).toLegacyPath(),
                ],
                eventHandlerDebounced
            )
        );

        runFloatingAsyncAction(async () => {
            await delay(10);
            await debouncedHandler(abortController.signal);
        });
        return () => {
            abortController.abort();
            subscription?.unsubscribe();
        };
    }, [
        props.bindingPath,
        props.pathToChecked,
        dispatch,
        props.bindingPath,
        props.filteredUniqKey,
        props.pathToChecked,
        props.checkedValue,
        displayedInstances,
        filteringResultVersion,
        isFormReloading,
    ]);

    const value =
        counters.visibleCount === 0 || counters.checkedInVisibleCount === 0
            ? false
            : counters.visibleCount === counters.checkedInVisibleCount
              ? true
              : undefined;

    return (
        <SelectAllCheckbox
            {...props}
            isReadOnly={isReadOnly}
            disabled={multilineIsInLoading || disabled}
            value={value}
            isInProcessing={isInProcessing}
        />
    );
};

export const SelectAllCheckboxContainer = withResolvePaths(
    withDisabledByParent(
        withViewModelSelect(
            withEvaluators(
                connect(
                    mapStateToProps,
                    mapDispatchToProps
                )(
                    withTestPageObjectRegistration(
                        withFocusManagement(
                            withExternalMountingEventReporting(withControlDebugInfo(SelectAllCheckboxWrapper))
                        )
                    )
                )
            )
        )
    )
);
